import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAnalyticsEvents } from "@/hooks/useAnalyticsEvents";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PagePerformanceTableProps {
  startDate: string;
  endDate: string;
}

export const PagePerformanceTable = ({ startDate, endDate }: PagePerformanceTableProps) => {
  const { data: events, isLoading } = useAnalyticsEvents({ startDate, endDate, limit: 1000 });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Seiten</CardTitle>
          <CardDescription>Performance der meistbesuchten Seiten</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[250px]" />
        </CardContent>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Top Seiten
          </CardTitle>
          <CardDescription>Performance der meistbesuchten Seiten</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Keine Seitendaten verfügbar</p>
        </CardContent>
      </Card>
    );
  }

  // Aggregate page views
  const pageStats = new Map<string, { views: number; conversions: number }>();
  
  events.forEach(event => {
    if (!event.page_path) return;
    
    if (!pageStats.has(event.page_path)) {
      pageStats.set(event.page_path, { views: 0, conversions: 0 });
    }
    
    const stats = pageStats.get(event.page_path)!;
    
    if (event.event_name === 'page_view') {
      stats.views += 1;
    }
    
    if (event.event_name === 'lead_captured' || event.event_name === 'consultation_requested') {
      stats.conversions += 1;
    }
  });

  // Convert to array and calculate conversion rates
  const topPages = Array.from(pageStats.entries())
    .map(([page, stats]) => ({
      page,
      views: stats.views,
      conversions: stats.conversions,
      conversionRate: stats.views > 0 ? (stats.conversions / stats.views) * 100 : 0,
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  const getConversionBadge = (rate: number) => {
    if (rate >= 5) return <Badge variant="default">Hoch</Badge>;
    if (rate >= 2) return <Badge variant="secondary">Mittel</Badge>;
    return <Badge variant="outline">Niedrig</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Top Seiten
        </CardTitle>
        <CardDescription>Performance der meistbesuchten Seiten</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Seite</TableHead>
              <TableHead className="text-right">Views</TableHead>
              <TableHead className="text-right">Conversions</TableHead>
              <TableHead className="text-right">Conv. Rate</TableHead>
              <TableHead className="text-right">Performance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topPages.map((page, index) => (
              <TableRow key={page.page}>
                <TableCell className="font-mono text-xs max-w-[250px] truncate" title={page.page}>
                  {page.page}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {page.views.toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {page.conversions.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {page.conversionRate.toFixed(2)}%
                </TableCell>
                <TableCell className="text-right">
                  {getConversionBadge(page.conversionRate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
